import React, { useState, useCallback, useRef, useEffect } from 'react';
import classNames from 'classnames';

import { Menu, MenuContent, MenuItem, MenuLabel } from '../../../components';
import css from './SortByPopup.module.css';

const optionLabel = (options, key) => {
  const option = options.find(o => o.key === key);
  return option ? option.label : key;
};

const SortByIcon = props => {
  const classes = classNames(css.icon, props.className);
  // extra small arrow head (down)
  return (
    <svg className={classes} width="8" height="5" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.764 4.236c.131.13.341.13.472 0l2.666-2.667a.333.333 0 10-.471-.471L4 3.528l-2.43-2.43a.333.333 0 10-.471.471l2.665 2.667z"
        fill="green"
        stroke="currentColor"
        fillRule="evenodd"
      />
    </svg>
  );
};

/**
 * SortByPopup component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.menuLabelRootClassName] - Custom class that extends the default class for the menu label
 * @param {string} props.urlParam - The url param
 * @param {string} props.label - The label
 * @param {Function} props.onSelect - The function to handle the select
 * @param {Array<Object>} props.options - The options [{ key: string, label: string }]
 * @param {string} [props.initialValue] - The initial value
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
const SortByPopup = props => {
  const [isOpen, setIsOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const {
    rootClassName,
    className,
    menuLabelRootClassName,
    urlParam,
    label,
    options,
    initialValue,
    contentPlacementOffset = 0,
    onSelect,
    triggerLabel,
    triggerAriaLabel,
  } = props;

  useEffect(() => () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    if (isOpen) return;
    openTimer.current = setTimeout(() => setIsOpen(true), 200);
  }, [isOpen]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  const onToggleActive = isOpenParam => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    if (isOpenParam) setIsOpen(true);
  };

  const selectOption = (urlParameter, option) => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setIsOpen(false);
    onSelect(urlParameter, option);
  };

  // resolve menu label text and class
  const triggerIcon = props.triggerIcon;

  const menuLabel =
    triggerLabel !== undefined
      ? triggerLabel
      : label || (initialValue ? optionLabel(options, initialValue) : '');
  const menuAriaLabel =
    triggerAriaLabel || label || (initialValue ? optionLabel(options, initialValue) : '');

  const classes = classNames(rootClassName || css.root, className);
  const menuLabelClasses = classNames(menuLabelRootClassName);
  const iconArrowClassName = isOpen ? css.iconArrowAnimation : null;

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
    <Menu
      className={classes}
      useArrow={false}
      contentPlacementOffset={contentPlacementOffset}
      contentPosition="right"
      onToggleActive={onToggleActive}
      isOpen={isOpen}
      preferScreenWidthOnMobile
    >
      <MenuLabel rootClassName={menuLabelClasses} ariaLabel={menuAriaLabel}>
        <span className={css.menuLabelText}>{menuLabel}</span>
        {triggerIcon ? triggerIcon : <SortByIcon className={iconArrowClassName} />}
      </MenuLabel>
      <MenuContent className={css.menuContent}>
        {options.map(option => {
          // check if this option is selected
          const selected = initialValue === option.key;

          return (
            <MenuItem key={option.key}>
              <button
                className={classNames(css.menuItem, {
                  [css.menuItemActive]: selected,
                  [css.menuItemDisabled]: option.disabled,
                })}
                disabled={option.disabled}
                onClick={() => (selected ? null : selectOption(urlParam, option.key))}
              >
                {option.longLabel || option.label}
              </button>
            </MenuItem>
          );
        })}
      </MenuContent>
    </Menu>
    </div>
  );
};

export default SortByPopup;
